import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { gsap } from 'gsap';
import { ContactBtnModalComponent } from '../models/Contact-btn/button/contact-btn.component';
import { registerRsPlugins, RsMotionService } from '../shared/motion';

/** Icon treatment for a role's tile — either a short wordmark or an inline glyph. */
type RoleIcon = 'php' | 'dotnet' | 'dotnetcore' | 'react' | 'angular' | 'tester' | 'designer' | 'intern';

interface Role {
  title: string;
  /**
   * Passed straight through to <app-contact-btn-modal [post]>. These strings
   * reach the apply dialog via localStorage, so they are reproduced exactly as
   * they were — including the original "Intership/Trainee" spelling.
   */
  post: string;
  icon: RoleIcon;
  /** Wordmark shown inside the tile for the text-based marks. */
  mark?: string;
  experience: string;
  type: string;
  location: string;
  description: string;
}

interface Perk {
  title: string;
  text: string;
  icon: 'growth' | 'culture' | 'projects' | 'balance';
}

@Component({
  selector: 'app-careers',
  standalone: true,
  imports: [CommonModule, ContactBtnModalComponent],
  templateUrl: './careers.component.html',
  styleUrls: ['./careers.component.scss'],
})
export class CareersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('crRoot') crRoot?: ElementRef<HTMLElement>;

  displayStyle = 'none';

  /**
   * The nine open roles. Every title, meta value and description is the copy
   * that was already on the page; only the icon/mark fields are new, and they
   * are presentational.
   */
  readonly roles: Role[] = [
    {
      title: 'PHP Developer',
      post: 'PHP Developer',
      icon: 'php',
      mark: 'php',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        'A PHP developer is responsible for writing server-side web application logic. PHP developers usually develop back-end components, connect the application with the other (often third-party) web services, and support the front-end developers by integrating their work with the application.',
    },
    {
      title: '.Net Developer',
      post: '.Net Developer',
      icon: 'dotnet',
      mark: '.NET',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        ".NET developer is a software engineer who builds the software using Microsoft's . NET technologies. He is responsible for the design, implementation and development of the software products according to technical needs.",
    },
    {
      title: '.Net Core Developer',
      post: '.Net Core Developer',
      icon: 'dotnetcore',
      mark: '.NET Core',
      experience: '2 to 3 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        'NET Core Developer is an expert who can design and develop modern web applications all stacks like UI, Back-end, database, version control, server and APIs.',
    },
    // Kept from the original page, which had this role commented out:
    // {
    //   title: 'Ionic Developer', post: 'Ionic Developer', icon: 'react',
    //   experience: '1 to 2 year Experience', type: 'Full Time', location: 'Bilaspur',
    //   description: 'Ionic is the app platform for web developers. Build amazing mobile, web, and desktop apps all with one shared code base and open web standards.',
    // },
    {
      title: 'React Native Developer',
      post: 'React Native Developer',
      icon: 'react',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        "React components wrap existing native code and interact with native APIs via React's declarative UI paradigm and JavaScript. This enables native app development for whole new teams of developers, and can let existing native teams work much faster.",
    },
    {
      title: 'React js Developer',
      post: 'React JS Developer',
      icon: 'react',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        'React js developers design and implement user interface components for JavaScript-based web and mobile applications using the React open-source library ecosystem. These skilled front-end developers are involved in all stages of interface component design, from conception through to final testing.',
    },
    {
      title: 'Angular Developer',
      post: 'Angular Developer',
      icon: 'angular',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        'Angular developers are talented JavaScript developers who possess knowledge of theoretic concepts of software engineering. Angular developers work on the client side of the software, which is the core activity of the developer. They can build a full-fledged User Interface service for websites and web applications.',
    },
    {
      title: 'Manual & Automation Tester',
      post: 'Manual & Automation Tester',
      icon: 'tester',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        "Manual testing is the process of identifying bugs and defects in software without the help of software testing automation tools. In this procedure, QA manual testers execute test cases manually while considering the end user's perspective.",
    },
    {
      title: 'Web Designer',
      post: 'Web-Designer',
      icon: 'designer',
      experience: '1 to 2 year Experience',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        'Designing visual imagery for websites and ensuring that they are in line with branding for clients. Working with different content management systems. Communicating design ideas using user flows, process flows, site maps, and wire frames.',
    },
    {
      title: 'Internship/Trainee',
      post: 'Intership/Trainee',
      icon: 'intern',
      experience: 'Fresher',
      type: 'Full Time',
      location: 'Bilaspur',
      description:
        'Those who are looking for career opportunity / those who want to build there career in IT sector.',
    },
  ];

  /** "More Than Just a Job" cards. */
  readonly perks: Perk[] = [
    {
      title: 'Growth & Learning',
      text: 'Continuous learning opportunities and career growth.',
      icon: 'growth',
    },
    {
      title: 'Collaborative Culture',
      text: 'Work with passionate people in a collaborative environment.',
      icon: 'culture',
    },
    {
      title: 'Innovative Projects',
      text: 'Build impactful products that solve real-world problems.',
      icon: 'projects',
    },
    {
      title: 'Work-Life Balance',
      text: 'We value your time and believe in a healthy work-life balance.',
      icon: 'balance',
    },
  ];

  private ctx?: gsap.Context;
  private cleanups: Array<() => void> = [];

  constructor(
    private title: Title,
    private motion: RsMotionService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.title.setTitle('Careers | Resiliencesoft');
  }

  /* ------------------------------------------------------------- animation -- */

  ngAfterViewInit(): void {
    const root = this.crRoot?.nativeElement;
    if (!root || !this.motion.enabled) return;

    this.motion.arm();
    registerRsPlugins();

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => {
        // hero
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        tl.from('.cr-hero__eyebrow', { opacity: 0, y: 16, duration: 0.5 })
          .from('.cr-hero__title', { opacity: 0, y: 40, duration: 0.85 }, '-=0.25')
          .from('.cr-hero__lede', { opacity: 0, y: 20, duration: 0.6 }, '-=0.5')
          .from('.cr-fact', { opacity: 0, y: 18, duration: 0.5, stagger: 0.1 }, '-=0.35')
          .from(
            '.cr-hero__art',
            { opacity: 0, scale: 0.92, duration: 0.95, ease: 'power3.out' },
            '-=1.05'
          );

        // section headers
        gsap.utils.toArray<HTMLElement>('.cr-head').forEach((head) => {
          gsap.from(head.children, {
            opacity: 0,
            y: 22,
            duration: 0.6,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: { trigger: head, start: 'top 88%', once: true },
          });
        });

        // role cards
        gsap.from('.cr-role', {
          opacity: 0,
          y: 30,
          duration: 0.6,
          stagger: 0.06,
          ease: 'power3.out',
          clearProps: 'transform,opacity',
          scrollTrigger: { trigger: '.cr-roles__grid', start: 'top 88%', once: true },
        });

        // perk cards
        gsap.from('.cr-perk', {
          opacity: 0,
          y: 26,
          duration: 0.55,
          stagger: 0.09,
          ease: 'power3.out',
          clearProps: 'transform,opacity',
          scrollTrigger: { trigger: '.cr-perks__grid', start: 'top 90%', once: true },
        });
      }, root);

      const hero = root.querySelector<HTMLElement>('.cr-hero');
      if (hero) {
        this.cleanups.push(this.motion.trackPointer(hero), this.motion.pauseOffscreen(hero));
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.ctx?.revert();
    this.ctx = undefined;
  }

  /* --------------------------------------------- original logic, unchanged -- */

  closeModel() {
    let modal_t = document.getElementById('popup1') as any;

    modal_t.classList.remove('show');
    modal_t.classList.add('hidden');
    // modal_t.classList.remove('hidden')
  }

  openModel() {
    (document.getElementById('popup1') as any).click();
  }

  onSpace(event: any) {
    if (event.keyCode === 32 && !event.target.value) return false;
    return true;
  }
}
